import { CellContext, ColumnDef, SortingState } from "@tanstack/react-table";
import { EditIcon, TrashAltIcon } from "assets/icons";
import { t } from "i18next";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ItemsTable,
  Status,
  TableItemActionsControl,
  TableItemStatusControl
} from "ui";

import { DELIVERY_COMPANY_STATUSES_LIST } from "../../constants";
import { DeliveryCompanyListItem } from "../../deliveryCompanies.types";
import { CompanyAvatar } from "../CompanyAvatar";

import styles from "./CompaniesTable.module.scss";

interface CompaniesTableProps {
  companies: DeliveryCompanyListItem[];
  isLoading: boolean;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  onEdit: (company: DeliveryCompanyListItem) => void;
  onDelete: (company: DeliveryCompanyListItem) => void;
  onToggleActive: (company: DeliveryCompanyListItem, isActive: boolean) => void;
}

const CompaniesTable = ({
  companies,
  isLoading,
  sorting,
  setSorting,
  onEdit,
  onDelete,
  onToggleActive
}: CompaniesTableProps) => {
  const navigate = useNavigate();
  const [openedActionsIndex, setOpenedActionsIndex] = useState<number | null>(
    null
  );
  const [openedStatusIndex, setOpenedStatusIndex] = useState<number | null>(
    null
  );

  const handleStatusUpdate = (isActive: boolean) => {
    if (openedStatusIndex === null) return;
    onToggleActive(companies[openedStatusIndex], isActive);
    setOpenedStatusIndex(null);
  };

  const statusesList: Status[] = DELIVERY_COMPANY_STATUSES_LIST.map(
    (statusOption) => ({
      ...statusOption,
      onClick: () => handleStatusUpdate(statusOption.value)
    })
  );

  const columns: Array<
    ColumnDef<DeliveryCompanyListItem, DeliveryCompanyListItem[]>
  > = useMemo(
    () => [
      {
        id: "index",
        header: () => "#",
        size: 4,
        minSize: 4,
        cell: ({ row }) => (
          <span className={styles.companiesTable__index}>{row.index + 1}</span>
        ),
        enableSorting: false
      },
      {
        id: "name",
        accessorKey: "name",
        header: t("Company"),
        size: 22,
        minSize: 22,
        cell: ({ row }) => (
          <div className={styles.companiesTable__nameCell}>
            <CompanyAvatar
              id={row.original.id}
              name={row.original.name}
              avatarUrl={row.original.avatar?.url}
            />
            <span className={styles.companiesTable__name}>
              {row.original.name}
            </span>
          </div>
        )
      },
      {
        id: "drivers",
        header: t("Drivers"),
        size: 10,
        minSize: 10,
        cell: ({ row }) => (
          <span>
            {row.original.activeDriverCount}/{row.original.totalDriverCount}
          </span>
        ),
        enableSorting: false
      },
      {
        id: "capacity",
        header: t("Capacity"),
        size: 18,
        minSize: 18,
        cell: ({ row }) => {
          const used = row.original.capacityUsed;
          const max = row.original.capacity || 0;
          const ratio = max > 0 ? Math.min(used / max, 1) : 0;
          return (
            <div className={styles.companiesTable__capacity}>
              <div className={styles.companiesTable__capacityBar}>
                <div
                  className={styles.companiesTable__capacityFill}
                  style={{ width: `${ratio * 100}%` }}
                />
              </div>
              <span className={styles.companiesTable__capacityLabel}>
                {used}/{max}
              </span>
            </div>
          );
        },
        enableSorting: false
      },
      {
        id: "todayOrders",
        header: t("Today Orders"),
        size: 10,
        minSize: 10,
        cell: ({ row }) => row.original.todayOrdersCount,
        enableSorting: false
      },
      {
        id: "contact",
        header: t("Contact"),
        size: 14,
        minSize: 14,
        cell: ({ row }) => row.original.phoneNumber || "—",
        enableSorting: false
      },
      {
        id: "isActive",
        accessorKey: "isActive",
        header: t("Status"),
        size: 12,
        minSize: 12,
        cell: ({
          row
        }: CellContext<DeliveryCompanyListItem, DeliveryCompanyListItem[]>) => (
          <TableItemStatusControl
            rowIndex={row.index}
            activeStatusIndex={row.original.isActive ? 0 : 1}
            openedItemStatusesIndex={openedStatusIndex}
            setOpenedItemStatusesIndex={setOpenedStatusIndex}
            statusesList={statusesList}
          />
        )
      },
      {
        id: "actions",
        header: () => null,
        size: 6,
        minSize: 6,
        cell: ({ row }) => (
          <TableItemActionsControl
            rowIndex={row.index}
            openedItemOptionsIndex={openedActionsIndex}
            setOpenedItemOptionsIndex={setOpenedActionsIndex}
            actionsList={[
              {
                label: t("Edit"),
                Icon: <EditIcon fill="#090914" width={18} height={18} />,
                onClick: () => onEdit(row.original)
              },
              {
                label: t("Delete"),
                Icon: <TrashAltIcon fill="#ff0033" width={18} height={18} />,
                labelColor: "#ff0033",
                onClick: () => onDelete(row.original)
              }
            ]}
          />
        ),
        enableSorting: false
      }
    ],
    [
      companies,
      openedActionsIndex,
      openedStatusIndex,
      onEdit,
      onDelete,
      statusesList
    ]
  );

  return (
    <ItemsTable
      className={styles.companiesTable}
      columns={columns}
      data={companies}
      sorting={sorting}
      setSorting={setSorting}
      isLoading={isLoading}
      skeletonHeight={65}
      onRowClick={(company) => navigate(`/companies/${company.id}`)}
    />
  );
};

export default CompaniesTable;
